import * as THREE from 'three';
import { explode, spriteLayer, groundUnder, playerAimPoint, clamp, registerStreak, unregisterStreak } from './common.js';
import { MEANS } from '../MatchAuthority.js';

/**
 * Napalm Strike — called-in area denial along a line.
 *
 * The player marks a point by looking at it; an unseen aircraft runs the line
 * along the player's own facing and walks a string of blasts down it. What is
 * actually dangerous is what it leaves behind: a burning corridor that persists
 * for a quarter of a minute and denies a whole lane.
 *
 * The blasts themselves are `fx:explosion` — the shared FX system already owns
 * the fireball, the smoke, the spall and the dynamic light, and the camera rig
 * and post stack already listen for it, so a single event per blast buys the
 * entire moment. This module only owns the fire that is left over.
 */

export const TUNING = {
  callInDelay: 2.6, // s between the deploy and the first blast
  blasts: 9,
  spacing: 5.2, // m between blasts
  /** Where the first blast lands relative to the marked point, along the run. */
  nearOffset: 2.0,
  /** The mark is clamped into this band ahead of the player, measured along facing. */
  minMark: 8,
  maxMark: 42,
  blastInterval: 0.14, // s
  blastPower: 1.5,
  blastRadius: 7.5,
  blastDamage: 150,
  /** Fraction of blast damage the player takes from their own strike. */
  blastPlayerScale: 0.35,

  fireLife: 15, // s the corridor keeps burning
  fireRadius: 3.4, // m of lethal radius around each node
  fireDPS: 26,
  playerDPS: 13, // your own napalm hurts, but at half rate
  damageTick: 0.25, // s between damage applications

  /** Fire particles per node. Nine nodes at 26 is 234 points in one draw call. */
  puffsPerNode: 26,
  puffRise: 2.3, // m/s
  puffLife: 1.5,
  /**
   * Flame colour, deliberately over 1.0. The whole frame goes through ACES tone
   * mapping in the post stack, so a flame authored at LDR orange comes out as a
   * dull tan smudge — measured against a sunlit street it read as dust, not fire.
   * These are the values that survive the curve.
   */
  fireRGB: [3.1, 1.35, 0.42],
  fireSize: 3.4,

  smokePuffs: 8,
  smokeRise: 2.6,
  smokeLife: 3.4,

  lights: 3,
};

const _p = new THREE.Vector3();
const _q = new THREE.Vector3();
const _aim = new THREE.Vector3();

export class NapalmStrike {
  static id = 'napalm';

  constructor(ctx, sys) {
    this.ctx = ctx;
    this.sys = sys;
    this.nodes = [];
    this.lights = [];
    this._scratch = [];
    this._built = false;
    registerStreak(ctx, this, 'NAPALM');
  }

  _build() {
    if (this._built) return;
    this._built = true;
    // Two lights only; a burning corridor lit by nine point lights is both wrong
    // (fire is diffuse) and expensive.
    for (let i = 0; i < TUNING.lights; i++) {
      const l = new THREE.PointLight(0xff8434, 0, 26, 2);
      l.visible = false;
      this.ctx.scene.add(l);
      this.lights.push(l);
    }
  }

  deploy() {
    this._build();
    this._mark(_aim);

    const yaw = this.ctx.player?.yaw ?? 0;
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);

    // The marked point is where the run *starts*, and it runs away from the player
    // from there. Centring the run on the mark reads better on paper and is a trap
    // in practice: half the corridor lands behind the aim point, which at a short
    // mark is directly on top of the player. Measured, that killed a full-health
    // operator in under two seconds. The fire always walks away from you now.
    const near = TUNING.nearOffset;
    this.nodes.length = 0;
    for (let i = 0; i < TUNING.blasts; i++) {
      const d = near + i * TUNING.spacing;
      const x = _aim.x + fx * d;
      const z = _aim.z + fz * d;
      const y = groundUnder(this.ctx, x, z, _aim.y + 30, 60);
      this.nodes.push({
        x, y: y + 0.15, z,
        blown: false,
        seed: Math.random() * 100,
      });
    }

    this.t = 0;
    this.next = 0;
    this.fired = 0;
    this.damageAcc = 0;
    this.done = false;

    this.ctx.events.emit('audio:play', { id: 'ui_select', volume: 0.75, pitch: 0.5 });
    return true;
  }

  /**
   * Where the run starts.
   *
   * Naively this is "whatever the player is looking at", and that is wrong at the
   * two extremes. Looking at the horizon marks a rooftop 160 m away that the player
   * cannot see the far side of; looking at their own boots marks a point they are
   * standing on. So the mark is clamped into a usable band ahead of the player and
   * then dropped onto whatever surface is under it.
   */
  _mark(out) {
    playerAimPoint(this.ctx, TUNING.maxMark, out);
    const p = this.ctx.player;
    const yaw = p?.yaw ?? 0;
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);
    const dx = out.x - (p?.position?.x ?? 0);
    const dz = out.z - (p?.position?.z ?? 0);
    // Distance measured along the facing, so looking sideways-down cannot pull the
    // run behind the player.
    const along = dx * fx + dz * fz;
    const d = clamp(along, TUNING.minMark, TUNING.maxMark);
    out.x = (p?.position?.x ?? 0) + fx * d;
    out.z = (p?.position?.z ?? 0) + fz * d;
    out.y = groundUnder(this.ctx, out.x, out.z, (p?.position?.y ?? 0) + 30, 70);
    return out;
  }

  update(dt, elapsed) {
    this.t += dt;

    /* ---- the run ---- */
    if (this.fired < TUNING.blasts) {
      if (this.t >= TUNING.callInDelay + this.fired * TUNING.blastInterval) {
        const n = this.nodes[this.fired++];
        n.blown = true;
        n.burn = TUNING.fireLife;
        _p.set(n.x, n.y + 0.6, n.z);
        explode(this.ctx, _p, {
          power: TUNING.blastPower,
          radius: TUNING.blastRadius,
          damage: TUNING.blastDamage,
          playerScale: TUNING.blastPlayerScale,
          source: this,
          scratch: this._scratch,
        });
      }
    }

    /* ---- the corridor ---- */
    // Burned in the same pass as the run, so the first nodes are already alight
    // while the last blasts are still walking out. A strike that only catches fire
    // after the ninth explosion reads as two disconnected events.
    let anyBurning = false;
    for (const n of this.nodes) {
      if (n.burn > 0) {
        n.burn -= dt;
        if (n.burn > 0) anyBurning = true;
      }
    }
    if (!anyBurning && this.fired >= TUNING.blasts) {
      this.end();
      return false;
    }

    if (anyBurning) {
      this._burnDamage(dt);
      this._drawFire(elapsed ?? this.t);
    }
    return true;
  }

  /** Anything standing in the fire cooks, on a coarse tick rather than per frame. */
  _burnDamage(dt) {
    this.damageAcc += dt;
    if (this.damageAcc < TUNING.damageTick) return;
    const step = this.damageAcc;
    this.damageAcc = 0;

    const r2 = TUNING.fireRadius * TUNING.fireRadius;
    const match = this.ctx.match;
    /**
     * One sweep. Hostiles burn at `fireDPS`, the side that called the strike in at
     * the gentler `playerDPS` — which is what the separate player loop was actually
     * saying before there was a word for "your own side".
     */
    for (const reg of match?.living() ?? []) {
      const e = reg.actor;
      if (!e.position || typeof e.health !== 'number') continue;
      const friendly = reg.team === match.teamOf(this);
      const dps = friendly ? TUNING.playerDPS : TUNING.fireDPS;
      for (const n of this.nodes) {
        if (n.burn <= 0) continue;
        const dx = e.position.x - n.x;
        const dz = e.position.z - n.z;
        const dy = e.position.y - n.y;
        if (dx * dx + dz * dz > r2 || dy < -2 || dy > 3) continue;
        _q.set(dx, 0.3, dz).normalize();
        match.requestDamage({
          target: e,
          source: this,
          amount: dps * step,
          hitbox: null,
          point: new THREE.Vector3(e.position.x, e.position.y + 0.9, e.position.z),
          normal: _q.clone(),
          dir: _q.clone(),
          means: MEANS.BURN,
          weapon: 'napalm',
          splash: true,
        });
        break;
      }
    }
  }

  _drawFire(elapsed) {
    const fire = spriteLayer(this.ctx, 'fire', {
      count: 260, size: TUNING.fireSize, rgb: TUNING.fireRGB,
    });
    const smoke = spriteLayer(this.ctx, 'smoke', { count: 130, size: 3.6, additive: false });
    fire.begin();
    smoke.begin();

    let li = 0;
    for (const n of this.nodes) {
      if (n.burn <= 0) continue;
      // Fade the whole node out over its last two seconds.
      const fade = clamp(n.burn / 2, 0, 1);

      for (let i = 0; i < TUNING.puffsPerNode; i++) {
        // Deterministic per-puff phase: no per-frame RNG, no allocation, and the
        // flame keeps its shape instead of boiling.
        const ph = (elapsed * (0.55 + (i % 5) * 0.11) + n.seed + i * 0.37) % TUNING.puffLife;
        const k = ph / TUNING.puffLife;
        const ang = n.seed + i * 2.399;
        const rad = TUNING.fireRadius * 0.82 * Math.sqrt((i + 0.5) / TUNING.puffsPerNode);
        fire.add(
          n.x + Math.cos(ang) * rad + Math.sin(elapsed * 1.7 + i) * 0.18,
          n.y + 0.2 + k * TUNING.puffRise,
          n.z + Math.sin(ang) * rad + Math.cos(elapsed * 1.9 + i) * 0.18,
          // Lower per-puff alpha, more puffs: overlapping translucent sprites make
          // a body of flame, where fewer opaque ones make a row of dots.
          (1 - k) * (0.34 + 0.16 * Math.sin(elapsed * 9 + i * 1.7)) * fade,
          (0.5 + k * 1.15) * (0.62 + (i % 4) * 0.16)
        );
      }

      for (let i = 0; i < TUNING.smokePuffs; i++) {
        const ph = (elapsed * 0.36 + n.seed * 1.7 + i * 0.51) % TUNING.smokeLife;
        const k = ph / TUNING.smokeLife;
        const ang = n.seed * 2 + i * 1.86;
        fireSmoke(smoke, n, ang, k, fade);
      }

      // Three lights walk the burning nodes rather than one per node.
      if (li < this.lights.length && (li === 0 || Math.random() < 0.5)) {
        const l = this.lights[li++];
        l.visible = true;
        l.position.set(n.x, n.y + 1.3, n.z);
        l.intensity = (14 + Math.sin(elapsed * 11 + n.seed) * 5) * fade;
      }
    }
    for (; li < this.lights.length; li++) this.lights[li].visible = false;

    fire.end();
    smoke.end();
  }

  end() {
    for (const l of this.lights) {
      l.visible = false;
      l.intensity = 0;
    }
    for (const n of this.nodes) n.burn = 0;
    const fire = spriteLayer(this.ctx, 'fire');
    const smoke = spriteLayer(this.ctx, 'smoke');
    fire.begin();
    fire.end();
    smoke.begin();
    smoke.end();
  }

  status() {
    return this.fired < TUNING.blasts ? 'INBOUND' : 'BURNING';
  }

  dispose() {
    unregisterStreak(this.ctx, this);
    for (const l of this.lights) l.parent?.remove(l);
    this.lights.length = 0;
    this._built = false;
  }
}

function fireSmoke(layer, n, ang, k, fade) {
  const rad = 1.0 + k * 3.2;
  layer.add(
    n.x + Math.cos(ang) * rad,
    n.y + 1.2 + k * 6.5,
    n.z + Math.sin(ang) * rad,
    (1 - k) * 0.5 * fade,
    0.9 + k * 2.2
  );
}
